
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useHydratedDealerRatings } from "@/hooks/dealers/useHydratedDealerRatings";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/contexts/auth";
import { DealerRatingModal } from "./DealerRatingModal";

interface HydratedDealerRatingsProps {
  dealerId: string;
  dealerName: string;
  className?: string;
}

/**
 * Hydrated version of DealerRatings
 * This component uses the useHydratedDealerRatings hook to get ratings with SSR hydration support
 */
export function HydratedDealerRatings({ dealerId, dealerName, className }: HydratedDealerRatingsProps) {
  const { ratings, isLoading } = useHydratedDealerRatings(dealerId);
  const { isAuthenticated, user, isLoading: authLoading } = useAuthContext();
  const [showAll, setShowAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Debug temporaneo per verificare lo stato di autenticazione
  console.log('[HydratedDealerRatings] Auth Debug:', {
    isAuthenticated,
    user: user ? { id: user.id, email: user.email } : null,
    authLoading,
    dealerId,
    dealerName
  });
  
  // Utility to get user display name
  const getUserName = (rating: any) => {
    // If user_name exists in rating, use it, otherwise fallback to 'Utente' + ID fragment
    return rating.user_name || 'Utente ' + rating.id.substring(0, 4);
  };

  const displayedRatings = showAll ? ratings : ratings.slice(0, 3);

  return (
    <>
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recensioni Compro Oro</CardTitle>
          {isAuthenticated && (
            <Button size="sm" variant="outline" onClick={() => setIsModalOpen(true)}>
              Scrivi Recensione
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            // Loading skeleton
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <div className="flex items-center mt-1 sm:mt-0">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} className="h-4 w-4 mx-0.5" />
                          ))}
                        </div>
                      </div>
                      <Skeleton className="h-3 w-20 mb-2" />
                      <Skeleton className="h-4 w-full mb-1" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayedRatings && displayedRatings.length > 0 ? (
            <div className="space-y-4">
              {displayedRatings.map((rating) => (
                <div key={rating.id} className="border rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <Avatar>
                      <AvatarFallback>
                        {getUserName(rating).charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <div className="font-medium">
                          {getUserName(rating)}
                        </div>
                        <div className="flex items-center mt-1 sm:mt-0">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                "h-4 w-4",
                                i < rating.rating 
                                  ? "text-yellow-500 fill-current" 
                                  : "text-muted-foreground"
                              )}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground mb-1">
                        {new Date(rating.created_at).toLocaleDateString('it-IT')}
                      </div>
                      <p className="text-sm">{rating.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {ratings.length > 3 && (
                <div className="text-center mt-4">
                  <Button 
                    variant="link" 
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? 'Mostra Meno' : `Mostra Tutte (${ratings.length})`}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              Nessuna recensione disponibile per questo compro oro
            </div>
          )}
        </CardContent>
      </Card>
      {isAuthenticated && (
        <DealerRatingModal
            dealerId={dealerId}
            dealerName={dealerName}
            isOpen={isModalOpen}
            onOpenChange={setIsModalOpen}
        />
      )}
    </>
  );
}
